/**
 * BusinessUnitsDialog — Standorte/Geschaeftsbereiche pro Firma (Ebene 3).
 * z.B. StrongFit → Wetzikon / Rapperswil-Jona.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { MapPin, Plus, Trash2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BusinessUnitsDialog({ companyId, companyName, open, onOpenChange }) {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/companies/${companyId}/business-units`, { withCredentials: true });
      setUnits(r.data.items || []);
    } catch (e) {
      toast.error("Standorte konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const add = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/companies/${companyId}/business-units`,
        { name, description }, { withCredentials: true });
      toast.success(`"${name}" hinzugefügt`);
      setName(""); setDescription("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte nicht erstellt werden");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    try {
      await axios.delete(`${API}/business-units/${u.id}`, { withCredentials: true });
      toast.success(`"${u.name}" gelöscht`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="business-units-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#2563EB]" /> Standorte / Bereiche — {companyName}</DialogTitle>
          <DialogDescription>
            Ebene 3 des Marketing OS: z.B. Wetzikon, Rapperswil oder Privatkunden, Verwaltungen. Posts können einem Bereich zugeordnet und in der Gruppen-Übersicht ausgewertet werden.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input
              data-testid="unit-name-input"
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Name, z.B. Wetzikon"
              className="flex-1"
            />
            <Button data-testid="unit-add-btn" disabled={saving || !name.trim()} onClick={add} className="gap-1">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Hinzufügen
            </Button>
          </div>
          <Input
            data-testid="unit-description-input"
            value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Beschreibung (optional)"
          />
          <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="units-list">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {!loading && units.length === 0 && (
              <p className="text-xs text-[var(--studio-text-muted)]">Noch keine Standorte/Bereiche angelegt.</p>
            )}
            {units.map((u) => (
              <div key={u.id} className="flex items-center gap-2 border border-[var(--studio-border)] rounded-lg px-3 py-2" data-testid={`unit-row-${u.id}`}>
                <div className="flex-1">
                  <div className="text-sm font-medium">{u.name}</div>
                  {u.description && <div className="text-[11px] text-[var(--studio-text-muted)]">{u.description}</div>}
                </div>
                <Badge variant="outline" className="text-[10px]">{u.content_count} Posts</Badge>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(u)} data-testid={`unit-delete-${u.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
